import { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import useSWR from 'swr';
import { AuthContext } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { usePlatform, useScript } from '../../utils/hooks';
import { runAdmob, useAdsListener } from '../../services/admob';
import {
  getBattleRandomListUrl,
  getBattleRandomRefreshUrl,
  getBattleStartUrl,
  getStartAd3Url,
} from '../../api/generated/cat-runner/game-cat-runner/game-cat-runner.ts';
import CatRunningFriendBattleScreen from './components/CatRunningFriendBattleScreen';

export default function CatRunningBattlePage() {
  useScript('https://t1.kakaocdn.net/kakao_js_sdk/2.7.1/kakao.min.js');

  const navigate = useNavigate();
  const browserDeviceType = usePlatform();
  const { showErrorToast } = useToast();
  const { API_SERVER_DOMAIN, useAccessToken, fetchWithToken, appInfo } = useContext(AuthContext);
  const accessToken = useAccessToken();
  const deviceType = appInfo?.os === 'ios' || appInfo?.os === 'android' ? appInfo.os : browserDeviceType;

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isPendingRefreshAd, setIsPendingRefreshAd] = useState(false);
  const [challengingSlotNo, setChallengingSlotNo] = useState(null);
  const pendingRefreshAdTokenRef = useRef(null);

  const fetcher = useCallback(async (...args) => fetchWithToken(...args), [fetchWithToken]);
  const { data: randomData, mutate: mutateRandom } = useSWR(
    accessToken ? [`${API_SERVER_DOMAIN}${getBattleRandomListUrl()}`, accessToken] : null,
    fetcher,
    { revalidateOnFocus: false },
  );
  const { data: profile } = useSWR(
    accessToken ? [`${API_SERVER_DOMAIN}/auth/user/check`, accessToken] : null,
    fetcher,
    { revalidateOnFocus: false },
  );

  const inviteCode = profile?.userInfo?.refCode ?? '';
  const invitedFriendCount = profile?.userInfo?.inviteBuddyCount ?? 0;

  const slots = useMemo(() => (Array.isArray(randomData?.slots) ? randomData.slots : []), [randomData]);
  const snackCount = randomData?.snackCount ?? 0;
  const bestDistanceM = randomData?.state?.bestDistanceAllTime ?? 0;
  const freeRefreshAvailable = randomData?.randomRefreshFreeAvailable ?? false;
  const refreshAdRequired = randomData?.randomRefreshAdRequired ?? false;
  const refreshAvailable = (randomData?.randomRefreshAvailable ?? false) || freeRefreshAvailable;

  const doRefresh = useCallback(
    async (adToken) => {
      setIsRefreshing(true);
      try {
        const res = await fetch(`${API_SERVER_DOMAIN}${getBattleRandomRefreshUrl()}`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(adToken ? { adToken } : {}),
        });
        const payload = await res.json().catch(() => null);
        if (!res.ok || payload?.result === 'fail') {
          showErrorToast(payload?.message || '리프레시에 실패했어요.');
          return;
        }
        mutateRandom(payload, { revalidate: false });
      } catch (error) {
        console.error('backend api: error POST /cat-runner/battle/random/refresh', { error });
        showErrorToast('리프레시에 실패했어요.');
      } finally {
        setIsRefreshing(false);
      }
    },
    [API_SERVER_DOMAIN, accessToken, mutateRandom, showErrorToast],
  );

  useAdsListener(
    '',
    '',
    {
      task: 'cat-runner-random-refresh',
      pending: isPendingRefreshAd,
      onError: () => {
        pendingRefreshAdTokenRef.current = null;
        setIsPendingRefreshAd(false);
        showErrorToast('광고 재생에 실패했어요. 잠시 후 다시 시도해 주세요.');
      },
    },
    async () => {
      if (!isPendingRefreshAd) return;
      const adToken = pendingRefreshAdTokenRef.current;
      pendingRefreshAdTokenRef.current = null;
      setIsPendingRefreshAd(false);
      await doRefresh(adToken);
    },
  );

  const handleRefresh = useCallback(async () => {
    if (!accessToken || isRefreshing || isPendingRefreshAd) return;

    if (freeRefreshAvailable) {
      await doRefresh(null);
      return;
    }

    if (!refreshAdRequired) {
      showErrorToast('오늘 사용 가능한 리프레시를 모두 사용했어요.');
      return;
    }

    if (typeof window.flutter_inappwebview?.callHandler !== 'function') {
      showErrorToast('앱 광고 실행 핸들러를 찾지 못했어요.');
      return;
    }

    try {
      setIsRefreshing(true);
      const res = await fetch(`${API_SERVER_DOMAIN}${getStartAd3Url()}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ placement: 'randomRefresh', deviceType }),
      });
      const payload = await res.json().catch(() => null);
      if (!res.ok || payload?.result === 'fail') {
        showErrorToast(payload?.message || '광고를 불러오지 못했어요.');
        return;
      }
      pendingRefreshAdTokenRef.current = payload?.adToken ?? null;
      setIsPendingRefreshAd(true);
      runAdmob('', payload?.placementId ?? '');
    } catch (error) {
      console.error('backend api: error POST /cat-runner/ads/start', { error });
      showErrorToast('광고를 불러오지 못했어요.');
    } finally {
      setIsRefreshing(false);
    }
  }, [
    API_SERVER_DOMAIN,
    accessToken,
    deviceType,
    doRefresh,
    freeRefreshAvailable,
    isPendingRefreshAd,
    isRefreshing,
    refreshAdRequired,
    showErrorToast,
  ]);

  const handleChallenge = useCallback(
    async (slotNo) => {
      if (!accessToken) {
        showErrorToast('로그인 후 이용해 주세요.');
        return;
      }
      if (!slotNo || challengingSlotNo) return;

      setChallengingSlotNo(slotNo);
      try {
        const res = await fetch(`${API_SERVER_DOMAIN}${getBattleStartUrl()}`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ randomSlotNo: slotNo }),
        });
        const payload = await res.json().catch(() => null);
        if (!res.ok || payload?.result === 'fail' || !payload?.roundToken) {
          showErrorToast(payload?.message || '배틀 시작에 실패했어요.');
          return;
        }

        const battleRound = {
          roundToken: payload.roundToken,
          mode: payload.mode ?? 'BATTLE',
          runSeed: payload.runSeed,
          spawnManifest: payload.spawnManifest,
          opponent: payload.opponent,
        };
        // 테스트용: battle/start 서버 응답값으로 리플레이 테스트 화면에서 재생
        navigate('/cat-running/replay-test', { state: { battleRound } });
      } catch (error) {
        console.error('backend api: error POST /cat-runner/battle/start', { error });
        showErrorToast('배틀 시작에 실패했어요.');
      } finally {
        setChallengingSlotNo(null);
      }
    },
    [API_SERVER_DOMAIN, accessToken, challengingSlotNo, navigate, showErrorToast],
  );

  return (
    <CatRunningFriendBattleScreen
      inviteCode={inviteCode}
      invitedFriendCount={invitedFriendCount}
      snackCount={snackCount}
      bestDistanceM={bestDistanceM}
      randomSlots={slots}
      randomFreeRefresh={freeRefreshAvailable}
      randomRefreshAvailable={refreshAvailable}
      isRefreshing={isRefreshing || isPendingRefreshAd}
      onRefresh={handleRefresh}
      onChallenge={handleChallenge}
      challengingSlotNo={challengingSlotNo}
    />
  );
}
